import { useState } from 'react';
import { supabase } from '../supabase';

const CREAM    = '#F4EFE0';
const PAPER    = '#EAE3CE';
const INK      = '#0A0A0A';
const INKSOFT  = '#3D3A2E';
const RED_SHD  = '#CC0000';

const GLASS_BG    = 'rgba(255,255,255,0.04)';
const GLASS_BDR   = 'rgba(255,255,255,0.08)';
const GLASS_INPUT = 'rgba(255,255,255,0.06)';
const GLASS_IBDR  = 'rgba(255,255,255,0.12)';
const WHITE       = '#FFFFFF';
const DIM         = '#A0AEC0';
const CYAN        = '#00E5FF';
const PINK        = '#FF4D9E';

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
}

export default function SubscribeCTA({ title, subtitle, source, theme = 'dark' }) {
  const [email,      setEmail]      = useState('');
  const [status,     setStatus]     = useState('idle');
  const [fieldError, setFieldError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setFieldError('');

    if (!email.trim()) {
      setFieldError('Please enter your email.');
      return;
    }
    if (!isValidEmail(email)) {
      setFieldError('Please enter a valid email address.');
      return;
    }

    setStatus('loading');

    const { error } = await supabase
      .from('subscribers')
      .insert({ email: email.trim().toLowerCase(), source });

    if (error) {
      setStatus(error.code === '23505' ? 'duplicate' : 'error');
      return;
    }

    setStatus('success');
  }

  if (theme === 'cream') {
    return (
      <CreamCTA
        title={title} subtitle={subtitle}
        email={email} setEmail={setEmail}
        status={status} fieldError={fieldError}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <DarkCTA
      title={title} subtitle={subtitle}
      email={email} setEmail={setEmail}
      status={status} fieldError={fieldError}
      onSubmit={handleSubmit}
    />
  );
}

// ── Cream / CJP risograph variant ─────────────────────────────────────────────
function CreamCTA({ title, subtitle, email, setEmail, status, fieldError, onSubmit }) {
  const done = status === 'success' || status === 'duplicate';

  return (
    <div style={{
      background: CREAM,
      border:     `3px solid ${INK}`,
      boxShadow:  `6px 6px 0 ${INK}`,
      padding:    '1.6rem',
    }}>
      {/* Headline — flex-wrap so bell + long title stack gracefully on mobile */}
      <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.4rem 0.6rem', marginBottom: '0.45rem' }}>
        <span style={{ fontSize: '1.25rem', lineHeight: 1.3, flexShrink: 0 }}>🔔</span>
        <h2 style={{
          margin:        0,
          fontFamily:    "'Arial Black', 'Arial Bold', sans-serif",
          fontWeight:    900,
          fontSize:      'clamp(0.95rem, 3.2vw, 1.3rem)',
          color:         INK,
          textTransform: 'uppercase',
          letterSpacing: '-0.5px',
          lineHeight:    1.15,
        }}>
          {title}
        </h2>
      </div>

      <p style={{
        margin:     '0 0 1.25rem',
        fontSize:   '0.88rem',
        color:      INKSOFT,
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {subtitle}
      </p>

      {done ? (
        <div style={{
          display:       'flex',
          alignItems:    'center',
          gap:           '0.5rem',
          fontFamily:    "'Arial Black', sans-serif",
          fontWeight:    900,
          fontSize:      '1rem',
          color:         status === 'success' ? '#1F5A2E' : INKSOFT,
          textTransform: 'uppercase',
          letterSpacing: '-0.3px',
        }}>
          <span>{status === 'success' ? '✓' : '—'}</span>
          <span>{status === 'success' ? "You're on the list." : "You're already subscribed."}</span>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          {/* flex-wrap: input fills row, button goes full-width below on mobile */}
          <div className="flex flex-wrap gap-3">
            <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
              <span style={{
                position:      'absolute',
                left:          '0.75rem',
                top:           '50%',
                transform:     'translateY(-50%)',
                pointerEvents: 'none',
                fontSize:      '0.9rem',
                color:         INKSOFT,
              }}>
                ✉
              </span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={status === 'loading'}
                style={{
                  width:        '100%',
                  boxSizing:    'border-box',
                  padding:      '0.75rem 0.75rem 0.75rem 2.2rem',
                  background:   PAPER,
                  border:       `2px solid ${INK}`,
                  borderRadius: 0,
                  fontFamily:   "'IBM Plex Sans', sans-serif",
                  fontSize:     '0.95rem',
                  color:        INK,
                  outline:      'none',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto"
              style={{
                padding:       '0.75rem 1.5rem',
                background:    INK,
                color:         CREAM,
                border:        `2px solid ${INK}`,
                borderRadius:  0,
                fontFamily:    "'Arial Black', 'Arial Bold', sans-serif",
                fontWeight:    900,
                fontSize:      '0.88rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                cursor:        status === 'loading' ? 'wait' : 'pointer',
                boxShadow:     `3px 3px 0 ${RED_SHD}`,
                transition:    'transform 80ms ease, box-shadow 80ms ease',
                whiteSpace:    'nowrap',
              }}
              onMouseEnter={e => {
                if (status !== 'loading') {
                  e.currentTarget.style.transform = 'translate(2px,2px)';
                  e.currentTarget.style.boxShadow = `1px 1px 0 ${RED_SHD}`;
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = `3px 3px 0 ${RED_SHD}`;
              }}
            >
              {status === 'loading' ? 'Submitting…' : 'Submit Email'}
            </button>
          </div>

          {(fieldError || status === 'error') && (
            <p style={{
              margin:     '0.5rem 0 0',
              fontSize:   '0.8rem',
              color:      RED_SHD,
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              {fieldError || 'Something went wrong. Please try again.'}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

// ── Dark / glassmorphic variant ───────────────────────────────────────────────
function DarkCTA({ title, subtitle, email, setEmail, status, fieldError, onSubmit }) {
  const done = status === 'success' || status === 'duplicate';

  return (
    <div style={{ padding: '2.5rem 1.25rem' }}>
      <div style={{
        background:           GLASS_BG,
        border:               `1px solid ${GLASS_BDR}`,
        borderRadius:         12,
        backdropFilter:       'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding:              '2rem 1.75rem',
        maxWidth:             640,
        margin:               '0 auto',
      }}>
        {/* Headline — flex-wrap so bell + title stack gracefully on mobile */}
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.4rem 0.6rem', marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '1.15rem', lineHeight: 1.3, flexShrink: 0 }}>🔔</span>
          <h2 style={{
            margin:     0,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize:   'clamp(0.95rem, 2.8vw, 1.15rem)',
            color:      WHITE,
            lineHeight: 1.2,
          }}>
            {title}
          </h2>
        </div>

        <p style={{
          margin:     '0 0 1.25rem',
          fontSize:   '0.85rem',
          color:      DIM,
          fontFamily: "'Poppins', sans-serif",
        }}>
          {subtitle}
        </p>

        {done ? (
          <div style={{
            display:    'flex',
            alignItems: 'center',
            gap:        '0.5rem',
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize:   '0.95rem',
            color:      status === 'success' ? '#4ADE80' : DIM,
          }}>
            <span>{status === 'success' ? '✓' : '—'}</span>
            <span>{status === 'success' ? "You're on the list." : "You're already subscribed."}</span>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {/* flex-wrap: input fills row, button goes full-width below on mobile */}
            <div className="flex flex-wrap gap-3">
              <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                <span style={{
                  position:      'absolute',
                  left:          '0.75rem',
                  top:           '50%',
                  transform:     'translateY(-50%)',
                  pointerEvents: 'none',
                  fontSize:      '0.85rem',
                  color:         DIM,
                }}>
                  ✉
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={status === 'loading'}
                  style={{
                    width:        '100%',
                    boxSizing:    'border-box',
                    padding:      '0.7rem 0.75rem 0.7rem 2.1rem',
                    background:   GLASS_INPUT,
                    border:       `1px solid ${GLASS_IBDR}`,
                    borderRadius: 8,
                    fontFamily:   "'Poppins', sans-serif",
                    fontSize:     '0.9rem',
                    color:        WHITE,
                    outline:      'none',
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto"
                style={{
                  padding:      '0.7rem 1.4rem',
                  background:   `linear-gradient(135deg, ${CYAN}, ${PINK})`,
                  color:        '#0D0D1A',
                  border:       'none',
                  borderRadius: 8,
                  fontFamily:   "'Poppins', sans-serif",
                  fontWeight:   700,
                  fontSize:     '0.85rem',
                  cursor:       status === 'loading' ? 'wait' : 'pointer',
                  whiteSpace:   'nowrap',
                  transition:   'opacity 150ms ease',
                  opacity:      status === 'loading' ? 0.6 : 1,
                }}
              >
                {status === 'loading' ? 'Submitting…' : 'Notify Me'}
              </button>
            </div>

            {(fieldError || status === 'error') && (
              <p style={{
                margin:     '0.5rem 0 0',
                fontSize:   '0.78rem',
                color:      '#F87171',
                fontFamily: "'Poppins', sans-serif",
              }}>
                {fieldError || 'Something went wrong. Please try again.'}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
